import React from 'react';
import { Loader2 } from 'lucide-react';

const STATUS = {
  Draft: 'bg-slate-100 text-slate-600', 'Pending Approval': 'bg-amber-50 text-amber-700', Active: 'bg-emerald-50 text-emerald-700',
  Discharged: 'bg-slate-200 text-slate-600', Pending: 'bg-slate-100 text-slate-600', 'In Progress': 'bg-amber-50 text-amber-700',
  Completed: 'bg-sky-50 text-sky-700', Approved: 'bg-emerald-50 text-emerald-700', Incomplete: 'bg-amber-50 text-amber-700',
  'Completed (Exception)': 'bg-violet-50 text-violet-700', Unpaid: 'bg-amber-50 text-amber-700', 'Partially Paid': 'bg-sky-50 text-sky-700',
  Paid: 'bg-emerald-50 text-emerald-700', Overdue: 'bg-rose-50 text-rose-700', Cancelled: 'bg-slate-200 text-slate-500',
  valid: 'bg-emerald-50 text-emerald-700', issued: 'bg-emerald-50 text-emerald-700', voided: 'bg-rose-50 text-rose-700',
};

export const Badge = ({ s, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${STATUS[s] || 'bg-slate-100 text-slate-600'}`}>{s}</span>
);

export const Card = ({ children, className = '', ...p }) => (
  <div className={`bg-white rounded-xl border border-slate-200 ${className}`} {...p}>{children}</div>
);

export const PageHeader = ({ title, sub, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
    <div>
      <h1 className="text-2xl font-bold text-slate-900" data-testid="care-page-title">{title}</h1>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Btn = ({ children, variant = 'primary', loading, className = '', ...p }) => {
  const v = {
    primary: 'bg-teal-700 hover:bg-teal-800 text-white', ghost: 'bg-white border border-slate-300 hover:bg-slate-50 text-slate-700',
    danger: 'bg-rose-600 hover:bg-rose-700 text-white', dark: 'bg-[#181B30] hover:bg-[#262a48] text-white',
  }[variant];
  return (
    <button {...p} disabled={loading || p.disabled}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${v} ${className}`}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}{children}
    </button>
  );
};

export const Input = ({ label, testid, className = '', textarea, ...p }) => (
  <label className={`block ${className}`}>
    {label && <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>}
    {textarea
      ? <textarea data-testid={testid} rows={3} {...p} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600" />
      : <input data-testid={testid} {...p} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600" />}
  </label>
);

export const Select = ({ label, testid, options, className = '', ...p }) => (
  <label className={`block ${className}`}>
    {label && <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>}
    <select data-testid={testid} {...p} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-600/30">
      {options.map((o) => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  </label>
);

export const Loading = () => (
  <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-teal-700" /></div>
);

export const Empty = ({ children }) => <p className="text-sm text-slate-400 text-center py-10">{children}</p>;

export const Table = ({ cols, rows, testid, onRow }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
          {cols.map((c) => <th key={c.k} className={`px-4 py-3 font-semibold ${c.right ? 'text-right' : ''}`}>{c.h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined}
            className={`border-b border-slate-100 last:border-0 ${onRow ? 'cursor-pointer hover:bg-slate-50' : ''}`} data-testid={`${testid}-row-${i}`}>
            {cols.map((c) => <td key={c.k} className={`px-4 py-3 align-top ${c.right ? 'text-right tabular-nums' : ''}`}>{c.r ? c.r(r) : r[c.k]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <Empty>No records yet.</Empty>}
  </div>
);
